// ============================================
// APPLICATION - Boletas Hooks
// ============================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { boletasRepository } from '../../infrastructure/http/boletas.repository';
import { queryKeys } from '../query-keys';
import type { FiltrosBoletasDto, ActualizarBoletaDto } from '../../domain/dtos';
import { handleApiError } from '../../infrastructure/http/axios-client';
import { getFechaHoyLocal } from '../../shared/date-utils';

/**
 * Hook para obtener boletas con filtros de fecha
 * @param filtros - Filtros de fecha (desde, hasta)
 * @param enabled - Controla si la query se ejecuta
 */
export const useBoletasConFiltros = (filtros: FiltrosBoletasDto, enabled: boolean = true) => {
  // Un rango cuyo "hasta" quedó en el pasado es inmutable salvo edición/eliminación
  // manual, y esas mutaciones ya invalidan queryKeys.boletas.all. Puede quedar fresco
  // el resto de la sesión. El rango que incluye hoy sí puede recibir ventas nuevas.
  const esRangoCerrado = Boolean(filtros.hasta) && filtros.hasta < getFechaHoyLocal();

  return useQuery({
    queryKey: queryKeys.boletas.conFiltros(filtros),
    queryFn: () => boletasRepository.obtenerBoletasConFiltros(filtros),
    gcTime: esRangoCerrado ? 24 * 60 * 60 * 1000 : 30 * 60 * 1000,
    staleTime: esRangoCerrado ? Infinity : 60 * 1000,
    enabled,
    retry: 1,
    throwOnError: false,
    select: (data) => {
      // Ordenar por fecha descendente (más recientes primero)
      return [...data].sort((a, b) =>
        new Date(b.fecha).getTime() - new Date(a.fecha).getTime()
      );
    },
  });
};

/**
 * Hook para obtener detalle de una boleta
 * @param boletaId - ID de la boleta
 */
export const useBoletaDetalle = (boletaId: number) => {
  return useQuery({
    queryKey: queryKeys.boletas.detail(boletaId),
    queryFn: () => boletasRepository.obtenerBoletaPorId(boletaId),
    gcTime: 5 * 60 * 1000, // 5 minutos
    staleTime: 2 * 60 * 1000, // 2 minutos
    retry: 1,
    throwOnError: false,
  });
};

/**
 * Hook para eliminar boleta
 */
export const useEliminarBoleta = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (boletaId: number) => boletasRepository.eliminarBoleta(boletaId),
    onSuccess: () => {
      // Invalidar todas las queries de boletas para refrescar los datos
      queryClient.invalidateQueries({ queryKey: queryKeys.boletas.all });
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      throw apiError;
    },
  });
};

/**
 * Hook para actualizar boleta
 */
export const useActualizarBoleta = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boletaId, datos }: { boletaId: number; datos: ActualizarBoletaDto }) =>
      boletasRepository.actualizarBoleta(boletaId, datos),
    onSuccess: () => {
      // Invalidar todas las queries de boletas para refrescar los datos
      queryClient.invalidateQueries({ queryKey: queryKeys.boletas.all });
      // Editar el detalle puede sincronizar el monto de un fiado asociado (ver
      // UpdateSaleUseCase.sincronizarFiadoPagoUnico en el backend); sin esto el
      // módulo de fiados sigue mostrando el monto viejo hasta el próximo refetch.
      queryClient.invalidateQueries({ queryKey: queryKeys.fiados.all });
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      throw apiError;
    },
  });
};
